<!--
  ImportWizardStep3 — ขั้นที่ 3 "วันที่ (Dates) + โหมดนำเข้า" ของ Import Wizard (S12-P1)

  ⚠️ เจ้าของไฟล์นี้คือ Task 5-B เท่านั้น — Task อื่นห้ามแก้ (ดู §1.7 Parallel Conflict Zone)

  ── สัญญากับ ImportWizard.vue (ห้ามเปลี่ยนเอง) ───────────────────────────────
  props
    state      Object  (required)  อ็อบเจ็กต์สถานะกลางที่ views/GraphEditor.vue เป็นเจ้าของ (มติ D10)
                                   → อ่านอย่างเดียว ห้าม mutate ห้าม reassign
    graphKey   String  (required)
    canEdit    Boolean (false)
    mobile     Boolean (false)     = $vuetify.display.smAndDown ที่ parent คำนวณมาให้แล้ว

  emits
    patch   (fields: Object)   ทางเดียวที่เปลี่ยน state ได้
    notify  ({ text, colour })
    goto    (step: Number)     ปุ่มลัดเท่านั้น — ปุ่ม Back/Next ปกติ parent เป็นคน render

  ฟิลด์ของ state ที่ขั้นนี้ "เขียนได้" (ผ่าน patch)
    effectiveDate (String 'yyyy-MM-dd' หรือ '') · expiryDate (String) · importMode ('create'|'update')
    step3Blocked (Boolean) · step3BlockReason (String ที่ $t แล้ว)

  🔴 วันที่ใน state ต้องเป็นสตริง 'yyyy-MM-dd' เสมอ · หน้าจอแสดงเป็น 'yyyy/MM/dd' (✏️ 2026-10-19 ตามที่ผู้ใช้ขอ)
     input[type=date] บังคับรูปแบบการแสดงผลไม่ได้ (ตาม locale ของเครื่อง) จึงใช้ v-date-picker แทน
     แปลงไป-กลับกับ Date ของ picker ด้วย new Date(y, m - 1, d) / getFullYear·getMonth·getDate (เวลาเครื่อง) เท่านั้น
     ห้าม toISOString / toLocaleDateString เด็ดขาด (คืนวันที่ UTC — เวลาไทยช่วงเช้ามืดจะได้วันของเมื่อวาน)
     และยังกรองซ้ำด้วย isoOrEmpty() ก่อน patch ทุกครั้ง

  เมื่อวันสิ้นสุด < วันเริ่มใช้ → patch({ step3Blocked: true, step3BlockReason: '…' })
  parent จะ disable ปุ่ม wiz-step3-next ให้เองพร้อม tooltip จาก step3BlockReason (ห้ามยิงคำขอใด ๆ)

  importMode มีผลกับป้ายปุ่ม Create ที่ขั้น 5 ทันที (parent คำนวณเป็น computed ให้แล้ว — TC-29)

  testid ของขั้นนี้ต้องขึ้นต้น wiz- เท่านั้น — ห้ามใช้ wizard- และห้ามใช้ index ของ array
-->
<template>
  <div data-testid="wiz-step3">
    <div class="wiz-section">{{ $t('graphRagWizDatesIntro') }}</div>

    <!-- ── ช่วงวันที่ (ไม่บังคับทั้งคู่) ───────────────────────────── -->
    <v-row dense class="mt-3">
      <v-col cols="12" md="6">
        <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
          <template #activator="{ props }">
            <div v-bind="props">
              <!-- input[type=date] แสดงผลตาม locale ของเครื่องและบังคับรูปแบบไม่ได้ ⇒ ช่องอ่านอย่างเดียว + ปฏิทินของ Vuetify -->
              <v-menu v-model="effectiveMenu" :close-on-content-click="false" :disabled="!canEdit" location="bottom start">
                <template #activator="{ props: menuProps }">
                  <v-text-field
                    v-bind="menuProps"
                    :model-value="displayDate(state.effectiveDate)"
                    :label="effectiveLabel"
                    :disabled="!canEdit"
                    placeholder="yyyy/mm/dd"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    clearable
                    density="comfortable"
                    variant="outlined"
                    hide-details="auto"
                    data-testid="wiz-effective-date"
                    @click:clear="onEffectiveDate('')"
                  />
                </template>
                <v-date-picker
                  :model-value="effectivePickerDate"
                  :max="expiryPickerDate || undefined"
                  color="#C32E0E"
                  hide-header
                  data-testid="wiz-effective-picker"
                  @update:model-value="onEffectiveDate(localDateToIso($event)); effectiveMenu = false"
                />
              </v-menu>
            </div>
          </template>
        </v-tooltip>
      </v-col>

      <v-col cols="12" md="6">
        <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
          <template #activator="{ props }">
            <div v-bind="props">
              <!-- input[type=date] แสดงผลตาม locale ของเครื่องและบังคับรูปแบบไม่ได้ ⇒ ช่องอ่านอย่างเดียว + ปฏิทินของ Vuetify -->
              <v-menu v-model="expiryMenu" :close-on-content-click="false" :disabled="!canEdit" location="bottom start">
                <template #activator="{ props: menuProps }">
                  <v-text-field
                    v-bind="menuProps"
                    :model-value="displayDate(state.expiryDate)"
                    :label="endLabel"
                    :disabled="!canEdit"
                    :error="dateOrderInvalid"
                    placeholder="yyyy/mm/dd"
                    prepend-inner-icon="mdi-calendar"
                    readonly
                    clearable
                    density="comfortable"
                    variant="outlined"
                    hide-details="auto"
                    data-testid="wiz-end-date"
                    @click:clear="onExpiryDate('')"
                  />
                </template>
                <v-date-picker
                  :model-value="expiryPickerDate"
                  :min="effectivePickerDate || undefined"
                  color="#C32E0E"
                  hide-header
                  data-testid="wiz-end-picker"
                  @update:model-value="onExpiryDate(localDateToIso($event)); expiryMenu = false"
                />
              </v-menu>
            </div>
          </template>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-alert v-if="dateOrderInvalid" type="error" variant="tonal" density="compact" class="mt-2" role="alert" data-testid="wiz-date-error">
      {{ $t('graphRagWizDateOrder') }}
    </v-alert>

    <!-- ── โหมดนำเข้าเมื่อเจอโหนดเดิม ─────────────────────────────── -->
    <div class="wiz-section mt-5">{{ $t('graphRagWizOnExisting') }}</div>

    <v-tooltip :disabled="canEdit" :text="$t('graphRagReadOnly')" location="top">
      <template #activator="{ props }">
        <div v-bind="props">
          <v-radio-group :model-value="importMode" :disabled="!canEdit" class="mt-1" density="comfortable" hide-details @update:model-value="onImportMode">
            <v-radio value="create" color="#C32E0E" data-testid="wiz-import-mode-create">
              <template #label>
                <div class="wiz-mode-label">
                  <div class="wiz-mode-title">{{ $t('graphRagWizModeCreate') }}</div>
                  <div class="wiz-sub">{{ $t('graphRagWizModeCreateHint') }}</div>
                </div>
              </template>
            </v-radio>

            <v-radio value="update" color="#C32E0E" class="mt-2" data-testid="wiz-import-mode-update">
              <template #label>
                <div class="wiz-mode-label">
                  <div class="wiz-mode-title">{{ $t('graphRagWizModeUpdate') }}</div>
                  <div class="wiz-sub">{{ $t('graphRagWizModeUpdateHint') }}</div>
                </div>
              </template>
            </v-radio>
          </v-radio-group>
        </div>
      </template>
    </v-tooltip>

    <!-- ── ไฟล์ที่ backend บอกว่าเคยนำเข้าแล้ว (TC-53) ─────────────── -->
    <v-alert v-if="duplicateCount > 0" type="info" color="#C32E0E" variant="tonal" density="compact" class="mt-4" aria-live="polite" data-testid="wiz-duplicate-hint">
      {{ $t('graphRagWizDatesDuplicate', { count: duplicateCount }) }}
    </v-alert>
  </div>
</template>

<script>
/** รูปแบบเดียวที่ยอมให้ส่งขึ้น state — ISO 'yyyy-MM-dd' ล้วน */
const CONST_ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export default {
  name: 'ImportWizardStep3',
  props: {
    state: { type: Object, required: true },
    graphKey: { type: String, required: true },
    canEdit: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
  },
  emits: ['patch', 'notify', 'goto'],
  data() {
    return {
      effectiveMenu: false,
      expiryMenu: false,
    };
  },
  computed: {
    effectiveLabel() {
      return `${this.$t('graphRagWizEffectiveDate')} (${this.$t('graphRagWizOptional')})`;
    },
    endLabel() {
      return `${this.$t('graphRagWizEndDate')} (${this.$t('graphRagWizOptional')})`;
    },
    /** ค่าให้ v-date-picker — computed เพื่อไม่สร้าง Date ใหม่ทุกครั้งที่ render (ปฏิทินจะได้ไม่ดีดกลับเดือนเดิม) */
    effectivePickerDate() {
      return this.isoToLocalDate(this.state.effectiveDate);
    },
    expiryPickerDate() {
      return this.isoToLocalDate(this.state.expiryDate);
    },
    /** ค่าเริ่มต้นคือ create เสมอ (เผื่อ state ยังว่าง) */
    importMode() {
      return this.state.importMode === 'update' ? 'update' : 'create';
    },
    /** ISO 'yyyy-MM-dd' เรียงตามตัวอักษร = เรียงตามเวลา จึงเทียบสตริงตรง ๆ ได้ ไม่ต้องแปลงเป็น Date */
    dateOrderInvalid() {
      const from = this.isoOrEmpty(this.state.effectiveDate);
      const to = this.isoOrEmpty(this.state.expiryDate);

      return !!from && !!to && to < from;
    },
    /** ข้อความเหตุผลที่ parent เอาไปทำ tooltip ของ wiz-step3-next ('' = ไปต่อได้) */
    blockReason() {
      return this.dateOrderInvalid ? this.$t('graphRagWizDateOrder') : '';
    },
    duplicateCount() {
      return (this.state.files || []).filter((f) => f.session && f.session.isDuplicateFile).length;
    },
  },
  watch: {
    // immediate = ตอนเข้าขั้นนี้ครั้งแรกก็ซิงก์สถานะบล็อกให้ parent ทันที
    blockReason: {
      immediate: true,
      handler(reason) {
        const blocked = !!reason;

        if (this.state.step3Blocked === blocked && (this.state.step3BlockReason || '') === reason) return;

        this.patch({ step3Blocked: blocked, step3BlockReason: reason });
      },
    },
  },
  methods: {
    /** ทางเดียวที่ขั้นนี้เปลี่ยน state กลางได้ */
    patch(fields) {
      this.$emit('patch', fields);
    },
    notify(text, colour) {
      this.$emit('notify', { text, colour: colour || 'success' });
    },
    /** รับเฉพาะ ISO 'yyyy-MM-dd' — อย่างอื่น (รวมค่าว่างตอนล้างช่อง) กลายเป็น '' */
    isoOrEmpty(value) {
      return typeof value === 'string' && CONST_ISO_DATE.test(value) ? value : '';
    },
    /** 'yyyy-MM-dd' → 'yyyy/MM/dd' สำหรับแสดงในช่อง (ค่าใน state ยังเป็น ISO) */
    displayDate(iso) {
      return this.isoOrEmpty(iso).replace(/-/g, '/');
    },
    /** ISO → Date เที่ยงคืนตามเวลาเครื่อง ให้ v-date-picker (ห้าม new Date('yyyy-MM-dd') — ตีความเป็น UTC) */
    isoToLocalDate(iso) {
      const value = this.isoOrEmpty(iso);
      if (!value) return null;

      const [y, m, d] = value.split('-').map(Number);

      return new Date(y, m - 1, d);
    },
    /** Date จาก v-date-picker → ISO ตามเวลาเครื่อง */
    localDateToIso(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    },
    onEffectiveDate(value) {
      this.patch({ effectiveDate: this.isoOrEmpty(value) });
    },
    onExpiryDate(value) {
      this.patch({ expiryDate: this.isoOrEmpty(value) });
    },
    onImportMode(value) {
      this.patch({ importMode: value === 'update' ? 'update' : 'create' });
    },
  },
};
</script>

<style scoped>
.wiz-section {
  font-size: 15px;
}
.wiz-sub {
  font-size: 12px;
  color: #646464;
}
.wiz-mode-label {
  padding: 2px 0;
}
.wiz-mode-title {
  font-size: 14px;
}
</style>
